import { useCallback, useEffect, useState } from "react";
import { atom, useRecoilState, useRecoilValue } from "recoil";
import StdFormDialog from "components/common/form/StdFormDialog";
import { TRoute } from "models/RouteModel";
import { useUser } from "models/UserModel";
import { useAlertPop } from "components/common/AlertPop";
import {
  Box,
  FormControl,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useSharedPages } from "models/SharedPageModel";
import { useCards } from "models/CardModel";
import { useCardLayouts } from "models/CardLayoutModel";
import { useSearchMaster, useSearchOptions } from "models/SearchMasterModel";

const openAtom = atom<boolean>({
  key: "SharePageFormDialog/atomOpen",
  default: false,
});

const routeAtom = atom<TRoute>({
  key: "SharePageFormDialog/routeAtom",
  default: undefined,
});

export const SharePageFormStates = () => {
  return { openAtom, routeAtom };
};

const SharePageFormDialog = () => {
  const [open, setOpen] = useRecoilState(openAtom);
  const route = useRecoilValue(routeAtom);
  const { sharedPages, save, refetch } = useSharedPages();
  const sharedPage = sharedPages.find((p) => route && p.routeId === route.id);

  const { getUser } = useUser();
  const user = getUser();
  const { getRouteCards } = useCards();
  const { getRouteCardLayouts } = useCardLayouts();

  // status
  const [status, setStatus] = useState(true);
  useEffect(() => {
    sharedPage && setStatus(sharedPage.status);
  }, [sharedPage]);

  // shiten_cd
  const { getSearchMasterByProvideKey } = useSearchMaster();
  const ShitenSearchMaster = getSearchMasterByProvideKey("SHITEN_CD");
  const { data: ShitenData } = useSearchOptions(ShitenSearchMaster);
  const ShitenOptions = [{ SHITEN_CD: "_none", NAME: "- 未設定 -" }, ...ShitenData];
  const [shitenCD, setShitenCD] = useState("_none");
  useEffect(() => {
    setShitenCD(sharedPage && sharedPage.shitenCD !== "" ? sharedPage.shitenCD : "_none");
  }, [sharedPage]);
  const handleOnChangeShiten = useCallback((event: SelectChangeEvent) => setShitenCD(event.target.value), []);

  const handleOnClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const { alertError } = useAlertPop();

  const handleOnSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const data = new FormData(event.currentTarget);
      if (!user || !route) {
        handleOnClose();
        return;
      }
      const cards = await getRouteCards(route);
      const layouts = await getRouteCardLayouts(route);
      const result = await save(
        sharedPage ? "patch" : "post",
        sharedPage
          ? {
              ...sharedPage,
              title: String(data.get("title")),
              baseRoute: route,
              status,
              contents: { cards, layouts },
              shitenCD: shitenCD === "_none" ? undefined : shitenCD,
            }
          : {
              status,
              title: String(data.get("title")),
              createUserId: String(user?.uid),
              routeId: route.id,
              baseRoute: route,
              contents: {
                cards,
                layouts,
              },
              shitenCD: shitenCD === "_none" ? undefined : shitenCD,
            },
      );
      if (result && result.status === "fail") {
        const message = result.response.data.message;
        if (message.indexOf("existing_uuid")) alertError("このページは既に共有されているので、共有出来ません。", event);
        else alertError("エラーが発生しました。" + message, event);
        return;
      } else await refetch();
      handleOnClose();
    },
    [
      alertError,
      getRouteCardLayouts,
      getRouteCards,
      handleOnClose,
      refetch,
      route,
      save,
      sharedPage,
      shitenCD,
      status,
      user,
    ],
  );

  if (!route) return <></>;

  return (
    <StdFormDialog
      title="Share Page Form"
      openState={open}
      handleOnSubmit={handleOnSubmit}
      handleOnClose={handleOnClose}
    >
      <FormControl component="fieldset" fullWidth sx={{ marginTop: 2, marginBottom: 2 }}>
        <FormLabel component="legend">元ページ名称</FormLabel>
        <Box sx={{ ml: 2 }}>{route.title}</Box>
      </FormControl>
      <FormControl component="fieldset" fullWidth sx={{ marginTop: 2, marginBottom: 2 }}>
        <FormLabel component="legend">共有名称</FormLabel>
        <TextField
          margin="normal"
          required
          fullWidth
          id="title"
          name="title"
          size="small"
          autoFocus
          defaultValue={sharedPage ? sharedPage.title : route.title}
          sx={{ mx: 2, mb: 0 }}
        />
      </FormControl>
      <FormControl component="fieldset" fullWidth sx={{ marginTop: 2, marginBottom: 2 }}>
        <FormLabel component="legend">ステータス</FormLabel>
        <ToggleButtonGroup
          value={status}
          exclusive
          onChange={() => setStatus((prev) => !prev)}
          size="small"
          aria-label="status"
          sx={{ my: 1, mx: 2, "& > button.Mui-selected": { fontWeight: "bold" } }}
        >
          <ToggleButton color="primary" value={true}>
            有効
          </ToggleButton>
          <ToggleButton color="error" value={false}>
            無効
          </ToggleButton>
        </ToggleButtonGroup>
      </FormControl>
      <FormControl fullWidth size="small" sx={{ mt: 2, width: "150px" }}>
        <InputLabel id="shiten-label">支店</InputLabel>
        <Select
          name="SHITEN_CD"
          label="支店"
          labelId="shiten-label"
          value={shitenCD}
          defaultValue={undefined}
          onChange={handleOnChangeShiten}
          sx={{ fontSize: "small" }}
        >
          {(ShitenOptions as { SHITEN_CD: any; NAME: string }[]).map((op, index) => {
            return (
              <MenuItem key={index} value={op.SHITEN_CD} dense={true}>
                {op.NAME}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </StdFormDialog>
  );
};

export default SharePageFormDialog;
